import React from 'react';
import Kendra from 'aws-sdk/clients/kendra';

import {
    Box,
    Container,
    Cards,
    TextContent,
} from '@cloudscape-design/components';
import { ResultTitle } from './ResultTitle';
import ResultText from './ResultText';
import { ResultFooter } from './ResultFooter';
import { isNullOrUndefined } from '../../facets/utils';
import { selectMostRecentUpdatedTimestamp } from 'src/components/kendra-search/utils';
import { useNavGraph } from 'src/components/providers/NavGraphProvider';
import { getLocaleDate } from 'src/helpers';
import { useTranslate } from 'src/i18n/useTranslate';
import ArtifactCardHeader from 'src/components/ArtifactCardHeader/ArtifactCardHeader';
import { useAuth } from 'src/components/providers/AuthProvider';
import { publishKendraClickThrough } from '../../../kendraMetrics';

interface DocumentResultsProps {
    results: Kendra.QueryResultItemList;
    viewStyle?: string;
    queryText?: string;
}

const getStringFromDocAttributes = (
    attributes: Record<string, any>,
    key: string
): string | undefined => attributes[key]?.StringValue;

export const DocumentResults = ({
    results,
    viewStyle = 'list-view',
    queryText = '',
}: DocumentResultsProps) => {
    const { user } = useAuth();

    if (isNullOrUndefined(results) || results.length === 0) {
        return null;
    }

    const isGridView = viewStyle === 'grid-view';

    if (isGridView) {
        const cardItems = results.map((result, index) => {
            const attributes = Object();
            if (!isNullOrUndefined(result.DocumentAttributes)) {
                result.DocumentAttributes!.forEach((attribute) => {
                    attributes[attribute.Key] = attribute.Value;
                });
            }

            const documentId = getStringFromDocAttributes(
                attributes,
                'documentId'
            );
            const topicId = getStringFromDocAttributes(attributes, 'topicId');

            let topicUrl: string;
            if (documentId && topicId) {
                topicUrl = `/${documentId}#/${topicId}/`;
            } else {
                throw new Error(
                    `Document ID: ${documentId} and/or Topid ID ${topicId} not present in Kendra search results: `
                );
            }

            const dateUpdated = getStringFromDocAttributes(
                attributes,
                'dateUpdated'
            );
            const lastUpdated = dateUpdated
                ? new Date(dateUpdated)
                : selectMostRecentUpdatedTimestamp(attributes);

            return {
                result,
                attributes,
                topicUrl,
                lastUpdated,
                documentId,
                resultPosition: index + 1,
            };
        });

        return (
            <Cards
                cardDefinition={{
                    header: (item) => (
                        <div
                            onClick={(e) => {
                                const target = e.target as HTMLElement;
                                if (
                                    target.tagName === 'A' ||
                                    target.closest('a')
                                ) {
                                    if (user?.hashedUserId && queryText) {
                                        publishKendraClickThrough(
                                            user.hashedUserId,
                                            queryText,
                                            item.documentId || '',
                                            item.resultPosition,
                                            item.topicUrl
                                                .split('#/')[1]
                                                .replace(/\/$/, ''),
                                            item.result.DocumentTitle?.Text ||
                                                ''
                                        );
                                    }
                                }
                            }}
                        >
                            <ArtifactCardHeader
                                item={{
                                    id: parseInt(
                                        getStringFromDocAttributes(
                                            item.attributes,
                                            'documentId'
                                        ) || '0'
                                    ),
                                    title:
                                        (getStringFromDocAttributes(
                                            item.attributes,
                                            'documentName'
                                        ) || 'Document') +
                                        (item.result.DocumentTitle?.Text
                                            ? ` > ${item.result.DocumentTitle.Text}`
                                            : ''),
                                    description:
                                        item.result.DocumentExcerpt?.Text || '',
                                    externalUrl: item.topicUrl,
                                    breadcrumbPath: getStringFromDocAttributes(
                                        item.attributes,
                                        'breadcrumbPath'
                                    ),
                                }}
                                testId={'kendra-search-card'}
                                openInSameTab={true}
                            />
                        </div>
                    ),
                    sections: [
                        {
                            id: 'content',
                            content: (item) => (
                                <ResultText
                                    text={item.result.DocumentExcerpt!}
                                    lastUpdated={item.lastUpdated}
                                />
                            ),
                        },
                        {
                            id: 'footer',
                            content: (item) => (
                                <ResultFooter
                                    attributes={item.attributes}
                                    topicUrl={item.topicUrl}
                                    lastUpdated={item.lastUpdated}
                                />
                            ),
                        },
                    ],
                }}
                cardsPerRow={[{ cards: 1 }, { minWidth: 500, cards: 3 }]}
                items={cardItems}
                trackBy={(item) => item.result.Id || ''}
            />
        );
    }

    const renderResults = (result: Kendra.QueryResultItem, index: number) => {
        const attributes = Object();
        if (!isNullOrUndefined(result.DocumentAttributes)) {
            result.DocumentAttributes!.forEach((attribute) => {
                attributes[attribute.Key] = attribute.Value;
            });
        }

        const documentId = getStringFromDocAttributes(attributes, 'documentId');
        const topicId = getStringFromDocAttributes(attributes, 'topicId');

        let topicUrl: string;
        if (documentId && topicId) {
            topicUrl = `/${documentId}#/${topicId}/`;
        } else {
            throw new Error(
                `Document ID: ${documentId} and/or Topid ID ${topicId} not present in Kendra search results: `
            );
        }

        const dateUpdated = getStringFromDocAttributes(
            attributes,
            'dateUpdated'
        );
        const lastUpdated = dateUpdated
            ? new Date(dateUpdated)
            : selectMostRecentUpdatedTimestamp(attributes);

        return (
            <Box
                padding={index === 0 ? { bottom: 's' } : { vertical: 's' }}
                key={index}
            >
                <Container key={result.Id}>
                    <ResultTitle
                        queryResultItem={result}
                        attributes={attributes}
                        topicUrl={topicUrl}
                        documentTitle={
                            getStringFromDocAttributes(
                                attributes,
                                'documentName'
                            ) || 'Document'
                        }
                        documentId={documentId || ''}
                        topicId={topicId}
                        topicName={result.DocumentTitle?.Text || ''}
                        queryText={queryText}
                        userId={user?.hashedUserId}
                        resultPosition={index + 1}
                    />
                    <ResultText
                        text={result.DocumentExcerpt!}
                        lastUpdated={lastUpdated}
                    />
                    <ResultFooter
                        attributes={attributes}
                        topicUrl={topicUrl}
                        lastUpdated={lastUpdated}
                    />
                </Container>
            </Box>
        );
    };

    return <Box>{results.map((r, i) => renderResults(r, i))}</Box>;
};
